import api from './api';
import { useAuthStore } from '../store/authStore';
import { yanxuanOrderItemCatalogPath } from '../utils/yanxuanBuyerAccess';
import type { YanxuanMallCatalogProduct } from '../utils/yanxuanMallCatalogFilter';
import type { YxOperatorFeeStatement } from './yanxuan.service';

const unwrap = <T>(res: any): T => (res.data?.data ?? res.data) as T;

export type YanxuanBuyerCatalogProduct = YanxuanMallCatalogProduct;

export type YxSalesOrderStatus =
  | 'DRAFT'
  | 'WAIT_CONFIRM'
  | 'CONFIRMED'
  | 'WAIT_PAY'
  | 'PAID'
  | 'CREDITED'
  | 'SPLITTING'
  | 'FULFILLING'
  | 'WAIT_RECEIVE'
  | 'RECEIVED'
  | 'CLOSED'
  | 'CANCELLED';

export type YxSupplyOrderStatus =
  | 'PENDING_ACCEPTANCE'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'PARTIAL_SHIPPED'
  | 'SHIPPED'
  | 'PARTIAL_RECEIVED'
  | 'RECEIVED'
  | 'CANCELLED'
  | 'CLOSED';

export interface YxSalesOrderItem {
  id: number;
  sales_order_id: number;
  yx_product_id?: number | null;
  yx_variant_id?: number | null;
  product_sku?: string | null;
  product_name: string;
  spec_name?: string | null;
  unit?: string | null;
  quantity: number;
  sale_price: number;
  line_amount: number;
  /** 行服务费，入账对象为本单严选供应商，不按原真供拆分 */
  service_fee_amount?: number | null;
  ex_factory_price_tax?: number | null;
  split_quantity?: number;
  shipped_quantity?: number;
  received_quantity?: number;
  cover_url?: string | null;
  /** 真供名称：商品默认真供（厂家）；履约渠道商不在此展示 */
  true_supplier_name?: string | null;
  /** 仅平台订单详情返回；采购接口不会暴露真供或成本 */
  split_suggestion?: {
    yx_supplier_id: number;
    ex_factory_price_tax: number;
    freight_amount_tax: number;
  } | null;
}

export interface YxSalesPayment {
  id: number;
  sales_order_id: number;
  status: string;
  amount: number;
  voucher_urls: string[];
  payer_note?: string | null;
  submitted_at?: string | null;
  confirmed_at?: string | null;
  reject_reason?: string | null;
  rejected_at?: string | null;
}

export interface YxInvoice {
  id: number;
  status: 'ISSUED' | 'SUBMITTED' | 'CONFIRMED' | 'REJECTED' | 'VOID';
  invoice_type: string;
  invoice_code?: string | null;
  invoice_no: string;
  buyer_title?: string;
  buyer_tax_no?: string | null;
  seller_title?: string;
  seller_tax_no?: string | null;
  amount: number;
  file_urls: string[];
  issued_at?: string | null;
  reject_reason?: string | null;
  void_reason?: string | null;
  supply_order_id?: number;
  sales_order_id?: number;
  supply_order?: YxSupplyOrder;
  supplier?: { id: number; name: string };
}

export interface YxSupplyOrderItem {
  id: number;
  supply_order_id: number;
  sales_order_item_id: number;
  product_name: string;
  spec_name?: string | null;
  quantity: number;
  ex_factory_price_tax: number;
  freight_amount_tax: number;
  line_amount: number;
  shipped_quantity?: number;
  received_quantity?: number;
}

export interface YxSupplyOrder {
  id: number;
  supply_no: string;
  sales_order_id: number;
  yx_supplier_id: number;
  status: YxSupplyOrderStatus;
  payment_status: string;
  payable_amount: number;
  ex_factory_amount_tax: number;
  freight_amount_tax: number;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  receiver_address?: string | null;
  reject_reason?: string | null;
  supplier?: { id: number; name: string; code?: string; short_name?: string | null };
  items?: YxSupplyOrderItem[];
  shipments?: YxShipment[];
  invoices?: YxInvoice[];
  payments?: YxSupplyPayment[];
  payer_yx_supplier_id?: number | null;
  payer_supplier?: { id: number; name: string; short_name?: string | null } | null;
  bank_account_snapshot?: Record<string, unknown> | null;
  sales_order?: {
    id: number;
    order_no: string;
    status: string;
    receiver_name?: string | null;
    receiver_phone?: string | null;
    receiver_address?: string | null;
  } | null;
  updated_at?: string;
}

export interface YxShipment {
  id: number;
  sales_order_id: number;
  supply_order_id?: number;
  logistics_company?: string | null;
  logistics_no?: string | null;
  shipped_at?: string;
  track_state?: string | null;
  signed_at?: string | null;
  items?: Array<{
    id: number;
    supply_order_item_id: number;
    sales_order_item_id: number;
    quantity: number;
    received_quantity?: number;
  }>;
}

export interface YxSalesOrder {
  id: number;
  order_no: string;
  buyer_org_id: number;
  buyer_user_id: number;
  status: YxSalesOrderStatus;
  settlement_mode: string;
  payment_status: string;
  seller_name?: string | null;
  /** 未税销售合计（新单）；历史单可能为含税挂牌合计 */
  sale_amount: number;
  freight_amount: number;
  /** 下单时平台订单税率百分数快照 */
  tax_rate?: number | string | null;
  /** 税额 */
  tax_amount?: number | string | null;
  total_amount: number;
  receiver_name?: string | null;
  receiver_phone?: string | null;
  receiver_address?: string | null;
  remark?: string | null;
  invoice_snapshot?: { invoice_type?: string; title?: string; tax_no?: string | null } | null;
  items?: YxSalesOrderItem[];
  payments?: YxSalesPayment[];
  supply_orders?: YxSupplyOrder[];
  shipments?: YxShipment[];
  sales_invoices?: YxInvoice[];
  statement_id?: number | null;
  statement_due_at?: string | null;
  statement?: YxSalesStatement | null;
  statement_status?: string | null;
  statement_payment_status?: string | null;
  after_sale_status?: string | null;
  after_sale_supplier_status?: string | null;
  after_sale_type?: string | null;
  after_sale_items?: Array<{ sales_order_item_id: number; quantity: number; amount: number }> | null;
  after_sale_amount?: number | string | null;
  after_sale_reason?: string | null;
  after_sale_return_logistics_company?: string | null;
  after_sale_return_logistics_no?: string | null;
  after_sale_return_voucher_url?: string | null;
  after_sale_refund_voucher_url?: string | null;
  has_issued_invoice?: boolean;
  lane?: string | null;
  payment_days_snapshot?: number | null;
  buyer_org_name?: string | null;
  /** 采购项目名称（需求成单带入） */
  project_name?: string | null;
  /** 供应商开票用：采购组织开票档案（列表/详情 decorate 注入） */
  buyer_invoice_profile?: {
    name?: string | null;
    invoice_title?: string | null;
    tax_number?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
    address?: string | null;
    contact_phone?: string | null;
  } | null;
  confirmed_at?: string | null;
  paid_at?: string | null;
  received_at?: string | null;
  updated_at?: string;
  created_at?: string;
}

export interface YxSupplierMember {
  id: number;
  yx_supplier_id: number;
  user_id: number;
  org_id?: number | null;
  member_role: string;
  status: string;
  supplier?: { id: number; name: string; code: string };
}

export interface YxSupplyPayment {
  id: number;
  supply_order_id: number;
  yx_supplier_id: number;
  payment_no?: string | null;
  status: string;
  amount: number;
  voucher_urls: string[];
  paid_at?: string | null;
  payer_note?: string | null;
  bank_account_snapshot?: Record<string, unknown> | null;
  supply_order?: { id?: number; supply_no?: string | null } | null;
}

export interface YxCreditPolicy {
  buyer_org_id: number;
  org_name?: string | null;
  enabled: boolean;
  credit_limit: number;
  used_amount: number;
  available_amount: number;
  /** 付款日期：每月几号 */
  payment_day_of_month?: number;
  /** @deprecated 兼容旧字段 */
  payment_days_after_statement: number;
  remark?: string | null;
  policy_id?: number | null;
}

export interface YxSalesStatement {
  id: number;
  statement_no: string;
  buyer_org_id: number;
  buyer_org_name?: string | null;
  period_year: number;
  period_month: number;
  period_seq?: number;
  status: string;
  payment_status?: string;
  voucher_urls?: string[] | null;
  total_amount: number;
  paid_amount: number;
  due_at?: string | null;
  issued_at?: string | null;
  sent_at?: string | null;
  buyer_confirmed_at?: string | null;
  deemed_confirmed_at?: string | null;
  invoice_file_urls?: string[] | null;
  invoice_records?: Array<{
    invoice_no: string;
    invoice_type?: string | null;
    invoice_code?: string | null;
    file_urls: string[];
    amount?: number | null;
    remark?: string | null;
    created_at?: string | null;
    created_by?: number | null;
  }> | null;
  invoiced_at?: string | null;
  /** 采购方开票资料（账单级；月结开票用） */
  buyer_invoice_profile?: {
    name?: string | null;
    invoice_title?: string | null;
    tax_number?: string | null;
    bank_name?: string | null;
    bank_account?: string | null;
    address?: string | null;
    contact_phone?: string | null;
  } | null;
  /** 严选供应商收款账号（待付款打印/查看） */
  seller_payment_account?: {
    seller_id: number;
    seller_name?: string | null;
    account_name?: string | null;
    bank_name?: string | null;
    account_no?: string | null;
  } | null;
  seller_payment_accounts?: Array<{
    seller_id: number;
    seller_name?: string | null;
    account_name?: string | null;
    bank_name?: string | null;
    account_no?: string | null;
  }>;
  items?: Array<{
    id: number;
    sales_order_id: number;
    amount: number;
    sales_order?: YxSalesOrder;
  }>;
}

export async function downloadYanxuanExcel(url: string) {
  const res = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([res.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const cd = String(res.headers?.['content-disposition'] || '');
  const match = cd.match(/filename="?([^"]+)"?/);
  const filename = match?.[1] || `yanxuan-${Date.now()}.xlsx`;
  const href = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

export async function downloadYanxuanSellerPickList(params: {
  ids?: number[];
  supplier_lane?: string;
  supplier_track?: string;
  order_keyword?: string;
}) {
  const query = new URLSearchParams();
  if (params.ids?.length) query.set('ids', params.ids.join(','));
  if (params.supplier_lane) query.set('supplier_lane', params.supplier_lane);
  if (params.supplier_track) query.set('supplier_track', params.supplier_track);
  if (params.order_keyword) query.set('order_keyword', params.order_keyword);
  const qs = query.toString();
  await downloadYanxuanExcel(
    `/yanxuan/supplier/sales-orders/pick-list/export.xlsx${qs ? `?${qs}` : ''}`,
  );
}

export async function downloadYanxuanInternalThreePriceSales(params: {
  scope: 'supplier' | 'platform';
  ids?: number[];
  supplier_lane?: string;
  supplier_track?: string;
  order_keyword?: string;
  period_start?: string;
  period_end?: string;
  settlement_mode?: string;
}) {
  const query = new URLSearchParams();
  if (params.ids?.length) query.set('ids', params.ids.join(','));
  if (params.supplier_lane) query.set('supplier_lane', params.supplier_lane);
  if (params.supplier_track) query.set('supplier_track', params.supplier_track);
  if (params.order_keyword) query.set('order_keyword', params.order_keyword);
  if (params.period_start) query.set('period_start', params.period_start);
  if (params.period_end) query.set('period_end', params.period_end);
  if (params.settlement_mode) query.set('settlement_mode', params.settlement_mode);
  const qs = query.toString();
  const base =
    params.scope === 'platform'
      ? '/yanxuan/platform/sales-orders/internal-three-price/export.xlsx'
      : '/yanxuan/supplier/sales-orders/internal-three-price/export.xlsx';
  await downloadYanxuanExcel(`${base}${qs ? `?${qs}` : ''}`);
}

export async function fetchYanxuanBuyerCatalog() {
  return unwrap<YanxuanBuyerCatalogProduct[]>(await api.get('/yanxuan/buyer/catalog')) ?? [];
}

export async function fetchYanxuanBuyerCatalogProduct(id: number) {
  return unwrap<YanxuanBuyerCatalogProduct>(await api.get(`/yanxuan/buyer/catalog/${id}`));
}

/** 订单明细弹窗：按当前角色直达对应只读目录，避免非采购角色先打采购接口触发 403。 */
export async function fetchYanxuanOrderItemCatalogProduct(id: number) {
  const role = useAuthStore.getState().user?.role;
  return unwrap<YanxuanBuyerCatalogProduct>(
    await api.get(yanxuanOrderItemCatalogPath(role, id)),
  );
}

export async function fetchYanxuanBuyerSalesOrders(status?: string) {
  return (
    unwrap<YxSalesOrder[]>(
      await api.get('/yanxuan/buyer/sales-orders', { params: status ? { status } : undefined }),
    ) ?? []
  );
}

export async function fetchYanxuanBuyerSalesOrder(id: number) {
  return unwrap<YxSalesOrder>(await api.get(`/yanxuan/buyer/sales-orders/${id}`));
}

export async function commitYanxuanWaitConfirmOrder(
  id: number,
  payload: {
    settlement_mode: 'CASH' | 'MONTHLY';
    receiver_name?: string;
    receiver_phone?: string;
    receiver_address?: string;
    receiver_address_id?: number;
  },
) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/buyer/sales-orders/${id}/commit`, payload));
}

export async function replaceYanxuanWaitConfirmItems(
  id: number,
  items: Array<{ yx_product_id?: number; yx_variant_id?: number; quantity: number }>,
) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/buyer/sales-orders/${id}/items`, { items }));
}

export async function fetchYanxuanBuyerStatements() {
  return unwrap<YxSalesStatement[]>(await api.get('/yanxuan/buyer/statements')) ?? [];
}

export async function fetchYanxuanBuyerStatementBadge() {
  return unwrap<{ count: number }>(await api.get('/yanxuan/buyer/statements/badge'));
}

export async function fetchYanxuanBuyerStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.get(`/yanxuan/buyer/statements/${id}`));
}

export async function confirmYanxuanBuyerStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.post(`/yanxuan/buyer/statements/${id}/confirm`));
}

export async function submitYanxuanStatementVoucher(id: number, voucher_urls: string[]) {
  return unwrap(await api.post(`/yanxuan/buyer/statements/${id}/voucher`, { voucher_urls }));
}

export async function createYanxuanBuyerSalesOrder(payload: {
  items: Array<{ yx_product_id?: number; yx_variant_id?: number; quantity: number }>;
  receiver_name: string;
  receiver_phone: string;
  receiver_address: string;
  remark?: string;
  settlement_mode?: 'CASH' | 'MONTHLY';
  invoice_snapshot?: {
    invoice_type?: 'VAT_NORMAL' | 'VAT_SPECIAL' | 'ELECTRONIC';
    title: string;
    tax_no?: string;
  };
}) {
  return unwrap<YxSalesOrder>(await api.post('/yanxuan/buyer/sales-orders', payload));
}

export async function uploadYanxuanPaymentVoucher(file: File) {
  const form = new FormData();
  form.append('file', file);
  return unwrap<{ url: string }>(
    await api.post('/yanxuan/buyer/sales-orders/upload-voucher', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );
}

export async function submitYanxuanBuyerPayment(
  id: number,
  payload: { voucher_urls: string[]; amount?: number; payer_note?: string },
) {
  return unwrap(await api.post(`/yanxuan/buyer/sales-orders/${id}/payment`, payload));
}

export async function cancelYanxuanBuyerPendingCashOrder(id: number) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/buyer/sales-orders/${id}/cancel`));
}

export async function receiveYanxuanBuyerOrder(
  id: number,
  payload: {
    shipment_id?: number;
    items: Array<{ sales_order_item_id: number; quantity: number; supply_order_item_id?: number }>;
    remark?: string;
  },
) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/buyer/sales-orders/${id}/receive`, payload));
}

export async function fetchYanxuanPlatformSalesOrders(params?: {
  status?: string;
  payment_status?: string;
}) {
  return unwrap<YxSalesOrder[]>(await api.get('/yanxuan/platform/sales-orders', { params })) ?? [];
}

export async function fetchYanxuanPlatformSalesOrder(id: number) {
  return unwrap<YxSalesOrder>(await api.get(`/yanxuan/platform/sales-orders/${id}`));
}

export async function discardYanxuanWaitConfirmOrder(id: number) {
  return unwrap<{ id: number }>(await api.delete(`/yanxuan/platform/sales-orders/${id}`));
}

/** @deprecated 现结由严选供应商确认；平台接口会拒绝核销。 */
export async function confirmYanxuanPlatformPayment(id: number) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/platform/sales-orders/${id}/confirm-payment`));
}

export async function uploadYanxuanInvoice(file: File, actor: 'platform' | 'supplier') {
  const form = new FormData();
  form.append('file', file);
  return unwrap<{ url: string }>(
    await api.post(`/yanxuan/${actor}/invoices/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );
}

export async function issueYanxuanSalesInvoice(id: number, payload: {
  invoice_type?: string;
  invoice_code?: string;
  invoice_no: string;
  buyer_title?: string;
  buyer_tax_no?: string;
  amount?: number;
  issued_at?: string;
  file_urls: string[];
  remark?: string;
}) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/platform/sales-orders/${id}/invoices`, payload),
  );
}

export async function voidYanxuanSalesInvoice(
  salesOrderId: number,
  invoiceId: number,
  reason?: string,
) {
  return unwrap<YxSalesOrder>(
    await api.post(
      `/yanxuan/platform/sales-orders/${salesOrderId}/invoices/${invoiceId}/void`,
      { reason },
    ),
  );
}

export async function fetchYanxuanPlatformSupplyInvoices(status?: string) {
  return unwrap<YxInvoice[]>(
    await api.get('/yanxuan/platform/supply-invoices', {
      params: status ? { status } : undefined,
    }),
  ) ?? [];
}

export async function reviewYanxuanSupplyInvoice(
  id: number,
  action: 'confirm' | 'reject',
  reason?: string,
) {
  return unwrap<YxInvoice>(
    await api.post(`/yanxuan/platform/supply-invoices/${id}/${action}`, { reason }),
  );
}

export async function splitYanxuanPlatformOrder(
  id: number,
  lines: Array<{
    sales_order_item_id: number;
    yx_supplier_id: number;
    quantity: number;
    ex_factory_price_tax: number;
    freight_amount_tax?: number;
  }>,
) {
  return unwrap<YxSalesOrder>(await api.post(`/yanxuan/platform/sales-orders/${id}/split`, { lines }));
}

export async function fetchYanxuanPlatformSupplyOrders(params?: {
  status?: string;
  yx_supplier_id?: number;
}) {
  return unwrap<YxSupplyOrder[]>(await api.get('/yanxuan/platform/supply-orders', { params })) ?? [];
}

export async function cancelRejectedYanxuanSupplyOrder(id: number) {
  return unwrap(await api.post(`/yanxuan/platform/supply-orders/${id}/cancel-rejected`));
}

export async function fetchYanxuanSupplierMembers(yx_supplier_id?: number) {
  return (
    unwrap<YxSupplierMember[]>(
      await api.get('/yanxuan/platform/supplier-members', {
        params: yx_supplier_id ? { yx_supplier_id } : undefined,
      }),
    ) ?? []
  );
}

export async function bindYanxuanSupplierMember(payload: {
  yx_supplier_id: number;
  user_id: number;
  org_id?: number;
  member_role?: string;
}) {
  return unwrap<YxSupplierMember>(await api.post('/yanxuan/platform/supplier-members', payload));
}

export async function unbindYanxuanSupplierMember(id: number) {
  return unwrap(await api.delete(`/yanxuan/platform/supplier-members/${id}`));
}

export interface YanxuanOpsUser {
  id: number;
  username: string;
  real_name: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
}

/** 运营台只读：严选管理员名单 */
export async function fetchYanxuanOpsUsers() {
  return unwrap<YanxuanOpsUser[]>(await api.get('/yanxuan/platform/ops-users')) ?? [];
}

export interface YxSellerWorkbenchMember extends YxSupplierMember {
  phone?: string | null;
  real_name?: string | null;
  username?: string | null;
  user_active?: boolean | null;
  created_user?: boolean;
}

export async function fetchYanxuanSellerWorkbenchMembers() {
  return (
    unwrap<YxSellerWorkbenchMember[]>(await api.get('/yanxuan/supplier/members')) ?? []
  );
}

export async function createYanxuanSellerWorkbenchMember(payload: {
  phone: string;
  real_name: string;
  password?: string;
  member_role?: string;
}) {
  return unwrap<YxSellerWorkbenchMember>(
    await api.post('/yanxuan/supplier/members', payload),
  );
}

export async function unbindYanxuanSellerWorkbenchMember(id: number) {
  return unwrap(await api.delete(`/yanxuan/supplier/members/${id}`));
}

/** 登录探测用：单独 5s 超时，避免探测慢时卡住登录跳转（axios 超时不弹 toast，调用方 catch 后走 1.0 路径） */
export const YANXUAN_SUPPLIER_CAPABILITY_PROBE_TIMEOUT_MS = 5000;

export async function fetchYanxuanSupplierCapability(options?: { timeout?: number }) {
  return unwrap<{
    enabled: boolean;
    is_channel_seller?: boolean;
    is_factory_supplier?: boolean;
    suppliers: Array<{ yx_supplier_id: number; member_role: string; code?: string; name?: string }>;
    factory_suppliers?: Array<{ yx_supplier_id: number; member_role: string; code?: string; name?: string }>;
    monthly_tripartite_agreement?: {
      required: boolean;
      accepted: boolean;
      agreement_slug: string;
      agreement_version: string;
      agreement_title: string;
    };
  }>(
    await api.get(
      '/yanxuan/supplier/capability',
      options?.timeout ? { timeout: options.timeout } : undefined,
    ),
  );
}

export async function fetchYanxuanSellerSalesOrders(params?: {
  page?: number;
  limit?: number;
  supplier_lane?: string;
  supplier_track?: string;
  order_keyword?: string;
}) {
  const data = unwrap<any>(await api.get('/yanxuan/supplier/sales-orders', { params }));
  if (Array.isArray(data)) {
    return { items: data as YxSalesOrder[], total: data.length, page: 1, limit: data.length };
  }
  return {
    items: (data?.items ?? []) as YxSalesOrder[],
    total: Number(data?.total || 0),
    page: Number(data?.page || 1),
    limit: Number(data?.limit || 20),
  };
}

/** 供应商只读预报：采购尚未 commit 的 WAIT_CONFIRM */
export async function fetchYanxuanSellerForecastOrders(params?: {
  page?: number;
  limit?: number;
  order_keyword?: string;
}) {
  const data = unwrap<any>(await api.get('/yanxuan/supplier/sales-orders/forecast', { params }));
  if (Array.isArray(data)) {
    return { items: data as YxSalesOrder[], total: data.length, page: 1, limit: data.length };
  }
  return {
    items: (data?.items ?? []) as YxSalesOrder[],
    total: Number(data?.total || 0),
    page: Number(data?.page || 1),
    limit: Number(data?.limit || 20),
  };
}

export async function fetchYanxuanSellerSalesOrderStats() {
  return (
    unwrap<{
      pendingRefund: number;
      refunded: number;
      immediate: Record<string, number>;
      monthly: Record<string, number>;
    }>(await api.get('/yanxuan/supplier/sales-orders/stats')) || emptyYanxuanSellerStats()
  );
}

function emptyYanxuanSellerStats() {
  return {
    pendingRefund: 0,
    refunded: 0,
    immediate: {},
    monthly: {},
  };
}

export async function batchShipYanxuanSellerSalesOrders(
  items: Array<{
    order_no: string;
    logistics_company: string;
    waybill_no?: string;
    logistics_no?: string;
    actual_freight_amount?: number;
    shipped_at?: string;
  }>,
) {
  return unwrap<{
    results: Array<{ order_no: string; ok: boolean; message: string }>;
    successCount: number;
    failCount: number;
  }>(await api.post('/yanxuan/supplier/sales-orders/batch-ship', { items }));
}

export async function downloadYanxuanBuyerSalesOrderExcel(id: number) {
  await downloadYanxuanExcel(`/yanxuan/buyer/sales-orders/${id}/export.xlsx`);
}

export async function downloadYanxuanPlatformSalesOrderExcel(id: number) {
  await downloadYanxuanExcel(`/yanxuan/platform/sales-orders/${id}/export.xlsx`);
}

export async function fetchYanxuanSellerSalesOrder(id: number) {
  return unwrap<YxSalesOrder>(await api.get(`/yanxuan/supplier/sales-orders/${id}`));
}

export async function fetchYanxuanSellerLogisticsTrack(id: number) {
  return unwrap<import('./logistics.service').LogisticsTrackOrderResult>(
    await api.get(`/yanxuan/supplier/sales-orders/${id}/logistics-track`),
  );
}

export async function fetchYanxuanBuyerLogisticsTrack(id: number) {
  return unwrap<import('./logistics.service').LogisticsTrackOrderResult>(
    await api.get(`/yanxuan/buyer/sales-orders/${id}/logistics-track`),
  );
}

export async function fetchYanxuanPlatformLogisticsTrack(id: number) {
  return unwrap<import('./logistics.service').LogisticsTrackOrderResult>(
    await api.get(`/yanxuan/platform/sales-orders/${id}/logistics-track`),
  );
}

export async function confirmYanxuanSellerPayment(id: number) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/confirm-payment`),
  );
}

export async function rejectYanxuanSellerPayment(id: number, reason: string) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/reject-payment`, { reason }),
  );
}

export async function shipYanxuanSellerSalesOrder(
  id: number,
  payload: {
    logistics_company: string;
    logistics_no: string;
    remark?: string;
    shipped_at?: string;
    items: Array<{ sales_order_item_id?: number; supply_order_item_id?: number; quantity: number }>;
  },
) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/shipments`, payload, {
      headers: { 'x-idempotency-key': `yx-ship-${id}-${Date.now()}` },
    }),
  );
}

export async function issueYanxuanSellerSalesInvoice(
  id: number,
  payload: {
    invoice_type?: string;
    invoice_code?: string;
    invoice_no: string;
    buyer_title?: string;
    buyer_tax_no?: string;
    amount?: number;
    file_urls?: string[];
    remark?: string;
  },
) {
  return unwrap(await api.post(`/yanxuan/supplier/sales-orders/${id}/invoices`, payload));
}

export async function reviewYanxuanSellerAfterSale(
  id: number,
  payload: { action: 'confirm' | 'reject'; reason?: string },
) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/after-sale/review`, payload),
  );
}

export async function generateYanxuanSellerStatements(payload?: {
  year?: number;
  month?: number;
  buyer_org_id?: number;
}) {
  return unwrap(await api.post('/yanxuan/supplier/statements/generate', payload || {}));
}

export async function fetchYanxuanSellerStatements(status?: string) {
  return (
    unwrap<YxSalesStatement[]>(
      await api.get('/yanxuan/supplier/statements', {
        params: status ? { status } : undefined,
      }),
    ) ?? []
  );
}

export async function sendYanxuanSellerStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.post(`/yanxuan/supplier/statements/${id}/send`));
}

export async function fetchYanxuanSellerStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.get(`/yanxuan/supplier/statements/${id}`));
}

export async function issueYanxuanSellerStatementInvoice(
  id: number,
  payload: {
    invoice_no: string;
    invoice_type?: string;
    invoice_code?: string;
    file_urls: string[];
    amount?: number;
    remark?: string;
    complete?: boolean;
  },
) {
  return unwrap<YxSalesStatement>(
    await api.post(`/yanxuan/supplier/statements/${id}/invoice`, payload),
  );
}

export async function completeYanxuanSellerStatementInvoice(
  id: number,
  payload?: { remark?: string },
) {
  return unwrap<YxSalesStatement>(
    await api.post(`/yanxuan/supplier/statements/${id}/invoice/complete`, payload || {}),
  );
}

export async function confirmYanxuanSellerStatement(id: number) {
  return unwrap(await api.post(`/yanxuan/supplier/statements/${id}/confirm-payment`));
}

export async function requestYanxuanBuyerAfterSale(
  id: number,
  payload: {
    reason: string;
    type?: 'REFUND_ONLY' | 'RETURN_REFUND';
    items?: Array<{ sales_order_item_id: number; quantity: number }>;
  },
) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/buyer/sales-orders/${id}/after-sale`, payload),
  );
}

export async function submitYanxuanBuyerReturnLogistics(
  id: number,
  payload: { logistics_company?: string; logistics_no: string; voucher_url?: string },
) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/buyer/sales-orders/${id}/after-sale/return-logistics`, payload),
  );
}

export async function confirmYanxuanBuyerAfterSaleRefund(id: number) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/buyer/sales-orders/${id}/after-sale/confirm-refund`),
  );
}

export async function uploadYanxuanSellerRefundVoucher(id: number, voucher_url: string) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/after-sale/refund-voucher`, { voucher_url }),
  );
}

/** 月结退货退款：供应商确认收货并冲减 */
export async function confirmYanxuanSellerReturnReceived(id: number) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/supplier/sales-orders/${id}/after-sale/confirm-return-received`),
  );
}

export async function fetchYanxuanSupplierOrders(status?: string) {
  return (
    unwrap<YxSupplyOrder[]>(
      await api.get('/yanxuan/supplier/orders', { params: status ? { status } : undefined }),
    ) ?? []
  );
}

export async function fetchYanxuanSupplierOrder(id: number) {
  return unwrap<YxSupplyOrder>(await api.get(`/yanxuan/supplier/orders/${id}`));
}

export async function acceptYanxuanSupplierOrder(id: number) {
  return unwrap<YxSupplyOrder>(
    await api.post(`/yanxuan/supplier/orders/${id}/accept`, null, {
      headers: { 'x-idempotency-key': `accept-${id}-${Date.now()}` },
    }),
  );
}

export async function rejectYanxuanSupplierOrder(id: number, reason?: string) {
  return unwrap<YxSupplyOrder>(
    await api.post(
      `/yanxuan/supplier/orders/${id}/reject`,
      { reason },
      { headers: { 'x-idempotency-key': `reject-${id}-${Date.now()}` } },
    ),
  );
}

export async function shipYanxuanSupplierOrder(
  id: number,
  payload: {
    logistics_company: string;
    logistics_no: string;
    remark?: string;
    items: Array<{ supply_order_item_id: number; quantity: number }>;
  },
) {
  return unwrap<YxSupplyOrder>(
    await api.post(`/yanxuan/supplier/orders/${id}/shipments`, payload, {
      headers: { 'x-idempotency-key': `ship-${id}-${Date.now()}` },
    }),
  );
}

export async function submitYanxuanSupplyInvoice(id: number, payload: {
  invoice_type?: string;
  invoice_code?: string;
  invoice_no: string;
  seller_title: string;
  seller_tax_no?: string;
  amount?: number;
  issued_at?: string;
  file_urls: string[];
  remark?: string;
}) {
  return unwrap<YxSupplyOrder>(
    await api.post(`/yanxuan/supplier/orders/${id}/invoices`, payload),
  );
}

export async function fetchYanxuanBuyerCreditPolicy() {
  return unwrap<YxCreditPolicy>(await api.get('/yanxuan/buyer/credit-policy'));
}

export async function fetchYanxuanBuyerPrintModel(id: number) {
  return unwrap<any>(await api.get(`/yanxuan/buyer/sales-orders/${id}/print-model`));
}

export async function fetchYanxuanPlatformSalesPrintModel(id: number) {
  return unwrap<any>(await api.get(`/yanxuan/platform/sales-orders/${id}/print-model`));
}

/** @deprecated 现结由严选供应商驳回；平台接口会拒绝核销。 */
export async function rejectYanxuanPlatformPayment(id: number, reason: string) {
  return unwrap<YxSalesOrder>(
    await api.post(`/yanxuan/platform/sales-orders/${id}/reject-payment`, { reason }),
  );
}

export async function fetchYanxuanCreditPolicies() {
  return unwrap<YxCreditPolicy[]>(await api.get('/yanxuan/platform/credit-policies')) ?? [];
}

export type YxOrderTaxSettings = {
  order_tax_rate: number;
  updated_at?: string;
  updated_by?: number | null;
};

export async function fetchYanxuanPlatformOrderTax() {
  return unwrap<YxOrderTaxSettings>(await api.get('/yanxuan/platform/settings/order-tax'));
}

export async function updateYanxuanPlatformOrderTax(payload: { order_tax_rate: number }) {
  return unwrap<YxOrderTaxSettings>(
    await api.post('/yanxuan/platform/settings/order-tax', payload),
  );
}

export async function fetchYanxuanBuyerOrderTax() {
  return unwrap<YxOrderTaxSettings>(await api.get('/yanxuan/buyer/settings/order-tax'));
}

export async function upsertYanxuanCreditPolicy(payload: {
  buyer_org_id: number;
  enabled?: boolean;
  payment_day_of_month?: number;
  /** @deprecated */
  payment_days_after_statement?: number;
  remark?: string;
}) {
  return unwrap(await api.post('/yanxuan/platform/credit-policies', payload));
}

export async function fetchYanxuanCashQueue() {
  return unwrap<YxSalesOrder[]>(await api.get('/yanxuan/platform/settlement/cash-queue')) ?? [];
}

export async function fetchYanxuanStatements(params?: { status?: string; buyer_org_id?: number }) {
  return unwrap<YxSalesStatement[]>(await api.get('/yanxuan/platform/statements', { params })) ?? [];
}

export async function fetchYanxuanStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.get(`/yanxuan/platform/statements/${id}`));
}

export async function generateYanxuanStatements(payload?: {
  year?: number;
  month?: number;
  buyer_org_id?: number;
}) {
  return unwrap(await api.post('/yanxuan/platform/statements/generate', payload || {}));
}

export async function sendYanxuanPlatformStatement(id: number) {
  return unwrap<YxSalesStatement>(await api.post(`/yanxuan/platform/statements/${id}/send`));
}

export async function recordYanxuanStatementReceipt(id: number, payload: { amount: number; remark?: string }) {
  return unwrap(await api.post(`/yanxuan/platform/statements/${id}/receipts`, payload));
}

export async function fetchYanxuanSupplyPayables(params?: { status?: string; yx_supplier_id?: number }) {
  return unwrap<YxSupplyOrder[]>(await api.get('/yanxuan/platform/supply-payments', { params })) ?? [];
}

export async function fetchYanxuanSellerSupplyPayables(params?: { status?: string }) {
  return unwrap<YxSupplyOrder[]>(await api.get('/yanxuan/supplier/supply-payables', { params })) ?? [];
}

export async function registerYanxuanSupplyPayment(
  id: number,
  payload: { amount?: number; payment_no?: string; voucher_urls: string[]; paid_at?: string; payer_note?: string },
) {
  return unwrap<YxSupplyPayment>(
    await api.post(`/yanxuan/platform/supply-orders/${id}/payments`, payload),
  );
}

export async function registerYanxuanSellerSupplyPayment(
  id: number,
  payload: { amount?: number; payment_no?: string; voucher_urls: string[]; paid_at?: string; payer_note?: string },
) {
  return unwrap<YxSupplyPayment>(
    await api.post(`/yanxuan/supplier/supply-orders/${id}/payments`, payload),
  );
}

export async function fetchYanxuanSupplierPayments() {
  return unwrap<YxSupplyPayment[]>(await api.get('/yanxuan/supplier/payments')) ?? [];
}

export async function confirmYanxuanSupplierPayment(id: number) {
  return unwrap<YxSupplyPayment>(await api.post(`/yanxuan/supplier/payments/${id}/confirm`));
}

export async function fetchYanxuanStatementPrintModel(id: number) {
  return unwrap<any>(await api.get(`/yanxuan/platform/statements/${id}/print-model`));
}

export async function fetchYanxuanBuyerStatementPrintModel(id: number) {
  return unwrap<any>(await api.get(`/yanxuan/buyer/statements/${id}/print-model`));
}

export async function fetchYanxuanSellerStatementPrintModel(id: number) {
  return unwrap<any>(await api.get(`/yanxuan/supplier/statements/${id}/print-model`));
}

export async function fetchYanxuanPlatformOperatorFeeStatements(status?: string) {
  const res = await api.get('/yanxuan/platform/operator-fee-statements', {
    params: status ? { status } : undefined,
  });
  return unwrap<YxOperatorFeeStatement[]>(res) ?? [];
}

export async function generateYanxuanPlatformOperatorFeeStatements(payload?: {
  year?: number;
  month?: number;
}) {
  return unwrap<{ year: number; month: number; created_or_updated: number }>(
    await api.post('/yanxuan/platform/operator-fee-statements/generate', payload || {}),
  );
}

export async function recordYanxuanPlatformOperatorFeeReceipt(
  id: number,
  body: { amount?: number; remark?: string },
) {
  return unwrap<YxOperatorFeeStatement>(
    await api.post(`/yanxuan/platform/operator-fee-statements/${id}/confirm-payment`, body),
  );
}

export async function confirmYanxuanPlatformOperatorFeePayment(
  id: number,
  body?: { amount?: number; remark?: string },
) {
  return unwrap<YxOperatorFeeStatement>(
    await api.post(`/yanxuan/platform/operator-fee-statements/${id}/confirm-payment`, body || {}),
  );
}

export async function uploadYanxuanPlatformOperatorFeeInvoice(file: File) {
  const form = new FormData();
  form.append('file', file);
  return unwrap<{ url: string }>(
    await api.post('/yanxuan/platform/operator-fee-statements/upload-invoice', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );
}

export async function issueYanxuanPlatformOperatorFeeInvoice(
  id: number,
  body: { invoice_no?: string; invoice_code?: string; file_urls?: string[]; remark?: string },
) {
  return unwrap<YxOperatorFeeStatement>(
    await api.post(`/yanxuan/platform/operator-fee-statements/${id}/invoice`, body),
  );
}

export async function fetchYanxuanSellerOperatorFeeStatements() {
  return unwrap<YxOperatorFeeStatement[]>(
    await api.get('/yanxuan/supplier/operator-fee-statements'),
  ) ?? [];
}

export async function uploadYanxuanSellerOperatorFeeVoucher(file: File) {
  const form = new FormData();
  form.append('file', file);
  return unwrap<{ url: string }>(
    await api.post('/yanxuan/supplier/operator-fee-statements/upload-voucher', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  );
}

export async function submitYanxuanSellerOperatorFeeVoucher(id: number, voucher_urls: string[]) {
  return unwrap<YxOperatorFeeStatement>(
    await api.post(`/yanxuan/supplier/operator-fee-statements/${id}/voucher`, { voucher_urls }),
  );
}

export async function confirmYanxuanSellerOperatorFeeInvoice(id: number) {
  return unwrap<YxOperatorFeeStatement>(
    await api.post(`/yanxuan/supplier/operator-fee-statements/${id}/confirm-invoice`),
  );
}
